import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Enums,
  ExpandedHuntPackageRecord,
  GeneratedContent,
  Hunt,
  HuntList,
  HuntPackageRecord,
  HuntPackagesAdded,
  HuntSummary,
  Subset,
} from '@cyborg-monorepo/hunter/common';
import { FilterDescriptor } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ExtendedState } from '../../hunt/overview/hunt-list/hunt-state-service';
import { HuntUrlService } from '../../hunt/overview/hunt-list/hunt-url-service';

@Injectable({
  providedIn: 'root',
})
export class HuntService {
  constructor(
    private httpClient: HttpClient,
    private huntUrlService: HuntUrlService,
  ) {}

  private apiUrl: string = environment.api + '/v2';

  public getHunts(state: ExtendedState): Observable<HuntList> {
    let params = new HttpParams();

    if (state.filter && state.filter.filters.length > 0)
      params = this.huntUrlService.stateToHttpParams(
        state.filter.filters as FilterDescriptor[],
      );

    params = params.append('page', state.skip / state.take + 1);
    params = params.append('pageSize', state.take);
    params = params.append('sortField', state.sort[0].field);
    params = params.append('sortDir', state.sort[0].dir.toUpperCase());
    params = params.append('archived', state.archived);

    return this.httpClient.get<HuntList>(`${this.apiUrl}/hunt`, {
      params: params,
    });
  }

  public archiveHunt(id: string): Observable<Hunt> {
    return this.httpClient.get<Hunt>(`${this.apiUrl}/hunt/${id}/archive`);
  }

  public unarchiveHunt(id: string): Observable<Hunt> {
    return this.httpClient.get<Hunt>(`${this.apiUrl}/hunt/${id}/unarchive`);
  }

  public createHunt(hunt: Partial<Hunt>): Observable<Hunt> {
    return this.httpClient.post<Hunt>(`${this.apiUrl}/hunt/`, hunt);
  }

  public generateHuntContent(
    huntPackages: { huntPackages: ExpandedHuntPackageRecord[] },
    id: string,
  ): Observable<Hunt> {
    return this.httpClient.put<Hunt>(
      `${environment.api}/hunt/${id}/generate`,
      huntPackages,
    );
  }

  public getHuntById(id: string): Observable<Hunt> {
    return this.httpClient.get<Hunt>(`${this.apiUrl}/hunt/${id}`);
  }

  public getTotalHunts(): Observable<{ result: number }> {
    return this.httpClient.get<{ result: number }>(
      `${this.apiUrl}/hunt/total-hunts`,
    );
  }

  public getCompletedHunts(): Observable<{ result: number }> {
    return this.httpClient.get<{ result: number }>(
      `${this.apiUrl}/hunt/completed-hunts`,
    );
  }

  public getQuickHunts(): Observable<Array<HuntSummary>> {
    return this.httpClient.get<Array<HuntSummary>>(`${this.apiUrl}/hunt/hunts`);
  }

  public addHuntPackagesToQuickHunt(
    id: string,
    huntPackages: HuntPackageRecord[],
  ): Observable<HuntPackagesAdded> {
    return this.httpClient.put<HuntPackagesAdded>(
      `${this.apiUrl}/hunt/${id}/add-hunt-packages`,
      huntPackages,
    );
  }

  public updateHunt(id: string, huntData: Partial<Hunt>): Observable<Hunt> {
    return this.httpClient.put<Hunt>(`${this.apiUrl}/hunt/${id}`, huntData);
  }

  public updateHuntStatus(
    id: string,
    huntData: { status: Enums.StatusEnum },
  ): Observable<Hunt> {
    return this.httpClient.put<Hunt>(
      `${this.apiUrl}/hunt/${id}/status`,
      huntData,
    );
  }

  public openHunt(id: string): Observable<Hunt> {
    return this.httpClient.get<Hunt>(`${this.apiUrl}/hunt/${id}/open`);
  }

  public closeHunt(id: string): Observable<Hunt> {
    return this.httpClient.get<Hunt>(`${this.apiUrl}/hunt/${id}/close`);
  }

  public finalizeHuntReport(id: string): Observable<Hunt> {
    return this.httpClient.get<Hunt>(`${this.apiUrl}/hunt/${id}/finalize`);
  }

  public updateHuntNotes(
    id: string,
    huntData: { notes: string },
  ): Observable<Hunt> {
    return this.httpClient.put<Hunt>(
      `${this.apiUrl}/hunt/${id}/notes`,
      huntData,
    );
  }

  public saveHuntReport(id: string, report: Subset<Hunt>): Observable<Hunt> {
    return this.httpClient.put<Hunt>(
      `${this.apiUrl}/hunt/${id}/save-report`,
      report,
    );
  }

  public updateHuntContent(
    id: string,
    context: {
      content: GeneratedContent;
      huntPackageUUID: string;
      environment: string;
      tool: string;
      category: string;
    },
  ): Observable<Hunt> {
    return this.httpClient.put<Hunt>(
      `${this.apiUrl}/hunt/${id}/content`,
      context,
    );
  }
}
